import React, { useEffect, useState, useRef } from 'react';
import cn from 'classnames';
import { CloseButton, Modal, Title, LazyImage } from '@landing/ui';
import { Slider } from 'components/UI/Slider';
import { useDeviceType } from 'utils/hooks';
import SliderControl from 'components/blocks/SliderControl';
import * as styles from './GalleryModal.module.scss';

export default function GalleryModal({
  title,
  initialImageIndex,
  isOpen,
  closeModalCallback,
  images,
  theme,
}) {
  const { deviceType } = useDeviceType();
  const topSlider = useRef(null);
  const bottomSlider = useRef(null);
  const showBottomSlider = deviceType !== 'mobile';

  useEffect(() => {
    if (isOpen && (theme !== 'office' || showBottomSlider)) {
      topSlider?.current?.slickGoTo(initialImageIndex - 1);
      bottomSlider?.current?.slickGoTo(initialImageIndex - 1);
    }
  }, [isOpen, deviceType]);

  const [state, setState] = useState({
    currentPhotoIndex: initialImageIndex,
    topSliderSettings: {
      arrows: false,
      fade: true,
      swipe: true,
      centerMode: true,
      centerPadding: 0,
      asNavFor: bottomSlider.current,
      className: 'sliderOffice top',
      initialSlide: initialImageIndex - 1,
    },
    bottomSliderSettings: {
      arrows: false,
      asNavFor: topSlider.current,
      className: 'sliderOffice bottom',
      initialSlide: initialImageIndex - 1,
    },
  });

  useEffect(() => {
    setState(prev => ({
      ...prev,
      currentPhotoIndex: initialImageIndex,
      topSliderSettings: {
        ...prev.topSliderSettings,
        initialSlide: initialImageIndex - 1,
      },
      bottomSliderSettings: {
        ...prev.bottomSliderSettings,
        slidesToShow: images.length > 7 ? 7 : images.length,
        initialSlide: initialImageIndex - 1,
      },
    }));
  }, [isOpen, initialImageIndex]);

  const { topSliderSettings, bottomSliderSettings } = state;

  const setCurrentPhotoIndex = (_, next) => {
    setState(prev => {
      return {
        ...prev,
        currentPhotoIndex: next + 1,
      };
    });
  };

  const togglePhoto = i => {
    topSlider.current.slickGoTo(i);

    if (showBottomSlider) {
      bottomSlider.current.slickGoTo(i);
    }
  };

  const toggleNext = () => {
    topSlider.current.slickNext();

    if (showBottomSlider) {
      bottomSlider.current.slickNext();
    }
  };

  const togglePrev = () => {
    topSlider.current.slickPrev();

    if (showBottomSlider) {
      bottomSlider.current.slickPrev();
    }
  };

  const displaySlider = () => (
    <>
      <div className="sliderOffice top">
        <Slider
          ref={topSlider}
          onSwipe={toggle => (toggle === 'left' ? toggleNext() : togglePrev())}
          beforeChange={setCurrentPhotoIndex}
          {...topSliderSettings}
        >
          {images.map(({ src, caption }) => (
            <figure key={src} className={styles.figure}>
              <LazyImage
                className={cn(
                  styles.img,
                  styles.imgBig,
                  showBottomSlider && styles.withBottomSlider,
                )}
                src={src}
                alt={caption}
              />
              <figcaption className={styles.imgCaption}>{caption}</figcaption>
            </figure>
          ))}
        </Slider>
      </div>
      {showBottomSlider && (
        <div className="sliderOffice bottom">
          <div className={styles.bottomSliderWrapper}>
            <Slider ref={bottomSlider} {...bottomSliderSettings}>
              {images.map(({ src, caption }, i) => (
                <div key={src} className="imageWrapper">
                  <LazyImage
                    className={styles.img}
                    src={src}
                    alt={caption}
                    onClick={() => togglePhoto(i)}
                  />
                </div>
              ))}
            </Slider>
          </div>
        </div>
      )}
      {images.length > 1 && (
        <SliderControl
          className={styles.sliderControls}
          theme={theme}
          current={state.currentPhotoIndex}
          to={images.length}
          handleNext={toggleNext}
          handlePrev={togglePrev}
        />
      )}
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      closeModalCallback={closeModalCallback}
      contentMod="withoutMargin"
      closeButton={false}
      theme="gallery"
      containerMod="tooHighGallery"
    >
      {onClose => (
        <>
          <CloseButton onClose={onClose} />
          <Title className={styles.title} tag="h2">
            {title}
          </Title>
          {displaySlider()}
        </>
      )}
    </Modal>
  );
}
